import React, { Component, createContext } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../../withRouters/withRoutersHook";

import MagnifyingGlassSVG from "../../../../Assets/svg/MagnifyingGlassSVG.svg";
import DotsThreeSVG from "../../../../Assets/svg/DotsThreeSVG.svg";
import PlaceholderImage1 from "../../../../Assets/svg/PlaceholderImage1.svg";
import DualRing from "../../../Animations/DualRing/DualRing";

import {
  FetchAllWhatAppMessagesAction,
  FetchIndividualWhatsAppMessagesAction,
  addPhoneNumberToRedux,
  addUserNameToRedux,
} from "../../../../Actions/Messaging";

export const UserContext = createContext();

class WhatsappChatList extends Component {
  static propTypes = {
    // Redux actions
    FetchAllWhatAppMessagesAction: PropTypes.func.isRequired,
    FetchIndividualWhatsAppMessagesAction: PropTypes.func.isRequired,
    addPhoneNumberToRedux: PropTypes.func.isRequired,
    addUserNameToRedux: PropTypes.func.isRequired,

    // Redux state
    allChatSessions: PropTypes.array,
    allChatSessionsFetchSuccess: PropTypes.bool,
    individualChatSession: PropTypes.object,

    // Router
    navigate: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,

    // Possibly from higher-level props, if any
    dispatch: PropTypes.func,
    session: PropTypes.object,
  };

  state = {
    isInChatList: null,
  };

  componentDidMount() {
    // Fetch all chat sessions initially
    this.props.FetchAllWhatAppMessagesAction();

    const isInCurrentPath = this.getPath();
    if (isInCurrentPath) {
      this.OpenWebSocket("all");
    }
    this.setState({ isInChatList: isInCurrentPath });

    if (this.props.session && this.props.session.user_name) {
      this.props.addUserNameToRedux(this.props.session.user_name);
    }
  }

  componentDidUpdate(prevProps) {
    const isInCurrentPath = this.getPath();
    const wasInPreviousPath = this.getPath(prevProps.location.pathname);

    if (isInCurrentPath && !wasInPreviousPath) {
      this.OpenWebSocket("all");
    } else if (!isInCurrentPath && wasInPreviousPath) {
      this.closeWebSocket();
    }
  }

  componentWillUnmount() {
    this.closeWebSocket();
  }

  OpenWebSocket = (path) => {
    if (!path) {
      console.error("WebSocket did not open due to missing path");
      return;
    }

    const wsUrl = `wss://${window.location.host}/ws/chat/${path}/`;
    console.log("Opening WebSocket connection to:", wsUrl);

    this.chatSocket = new WebSocket(wsUrl);

    this.chatSocket.onopen = () => {
      console.log("WebSocket connection opened");
    };

    this.chatSocket.onmessage = (e) => {
      console.log("Received WebSocket message:", e.data);
      // Fetch all messages again in case new data arrived
      this.props.FetchAllWhatAppMessagesAction();
    };

    this.chatSocket.onerror = (e) => {
      console.error("WebSocket error occurred:", e);
    };

    this.chatSocket.onclose = (e) => {
      console.log("WebSocket connection closed:", e.reason);
      // Optional: reconnect
      setTimeout(() => {
        console.log("Reconnecting WebSocket...");
        this.OpenWebSocket(path);
      }, 5000);
    };
  };

  closeWebSocket = () => {
    if (this.chatSocket) {
      this.chatSocket.close();
      console.log("WebSocket connection closed");
    }
  };

  getPath = (pathname = this.props.location.pathname) => {
    return pathname.startsWith("/dashboard/message-overview/whatsapp/");
  };

  /**
   * We updated this to also accept userName so we can dispatch
   * addUserNameToRedux.
   */
  continueChat = (phoneNumber, userName) => {
    console.log("Continue chat with:", phoneNumber);

    // Dispatch phone number to Redux (if you still want to store it)
    this.props.addPhoneNumberToRedux(phoneNumber);

    // Instead of storing userName in Redux, pass it directly in route state
    this.props.navigate(`/dashboard/message-overview/whatsapp/${phoneNumber}`, {
      state: { userName },
    });
  };

  getActiveSessionId = () => {
    const { pathname } = this.props.location;
    if (pathname.startsWith("/dashboard/message-overview/whatsapp/")) {
      return pathname.split("/").pop().replace(/\+/g, "");
    }
    return null;
  };

  formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date >= today) {
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    }
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date >= yesterday) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  renderChatSessions = () => {
    const { allChatSessions, allChatSessionsFetchSuccess } = this.props;
    const activeId = this.getActiveSessionId();

    if (!allChatSessionsFetchSuccess) {
      return (
        <div className="flex justify-center items-center w-full h-full py-10">
          <DualRing />
        </div>
      );
    }
    if (!allChatSessions || allChatSessions.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="font-inter text-sm text-gray-400">No conversations yet</div>
        </div>
      );
    }

    return allChatSessions.map((session, index) => {
      const lastMessage =
        session.messages.length > 0
          ? session.messages[session.messages.length - 1]
          : null;

      const isActive = activeId === session.user_phone_number.replace(/\+/g, "");

      const truncateMessage = (message) =>
        message && message.length > 28
          ? `${message.substring(0, 28)}...`
          : message;

      return (
        <button
          key={index}
          onClick={() =>
            this.continueChat(session.user_phone_number, session.user_name)
          }
          className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 transition-colors text-left w-full ${
            isActive ? "bg-blue-50" : "hover:bg-blue-50/40"
          }`}
        >
          <div className="relative shrink-0">
            <img
              src={session.profileImage || PlaceholderImage1}
              alt={session.user_name || "Contact"}
              className="h-10 w-10 rounded-full"
            />
            {isActive && (
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 border-2 border-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-baseline gap-1">
              <div className={`font-SFPro font-semibold text-sm truncate ${isActive ? "text-primary-blue" : "text-gray-900"}`}>
                {session.user_name || session.user_phone_number}
              </div>
              {lastMessage && (
                <span className="font-inter text-[10px] text-gray-400 shrink-0">
                  {this.formatTime(lastMessage.created_at)}
                </span>
              )}
            </div>
            <div className="flex justify-between items-center gap-1 mt-0.5">
              <div className="font-inter text-xs text-gray-500 truncate">
                {lastMessage ? truncateMessage(lastMessage.body) : "No messages yet"}
              </div>
              {session.unreadCount > 0 && (
                <span className="ml-1 shrink-0 flex items-center justify-center h-4 w-4 rounded-full bg-primary-blue text-white text-[10px] font-semibold">
                  {session.unreadCount}
                </span>
              )}
            </div>
          </div>
        </button>
      );
    });
  };

  render() {
    return (
      <div className="flex flex-col w-72 shrink-0 h-full border border-blue-100 rounded-xl bg-white overflow-hidden">
        {/* Header */}
        <div
          className="flex justify-between items-center px-5 py-4 border-b border-blue-100 rounded-t-xl"
          style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}
        >
          <div className="flex items-center gap-2">
            <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
              WhatsApp
            </p>
            {this.props.allChatSessions?.length > 0 && (
              <span className="inline-flex items-center bg-gray-50 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full border border-gray-100">
                {this.props.allChatSessions.length} chats
              </span>
            )}
          </div>
          <button className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors">
            <img src={DotsThreeSVG} alt="Options" className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="relative">
            <img
              className="absolute top-1/2 -translate-y-1/2 left-3 w-4 h-4 z-10"
              src={MagnifyingGlassSVG}
              alt="Search"
            />
            <input
              type="text"
              placeholder="Search"
              className="w-full py-2 pl-9 pr-3 text-sm font-inter bg-white border border-gray-200 focus:border-primary-blue outline-none rounded-lg transition-colors"
            />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex flex-col overflow-y-auto flex-1">
          {this.renderChatSessions()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  allChatSessions: state.messaging.allChatSessions,
  allChatSessionsFetchSuccess: state.messaging.allChatSessionsFetchSuccess,
  individualChatSession: state.messaging.individualChatSession,

  // If you want session data from Redux, map it here
  // session: state.someReducer.session
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchAllWhatAppMessagesAction,
    FetchIndividualWhatsAppMessagesAction,
    addPhoneNumberToRedux,
    addUserNameToRedux,
  })(WhatsappChatList)
);
